import type { ComponentType, ReactNode } from 'react';
import { Handle, Position, useStore } from '@xyflow/react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';
import { selectZoomDetail } from '../zoomLevel';
import { CollapseButton } from './CollapseButton';

/** Both handles sit at the node centre; edges render under the nodes, so they meet the card border. */
const CENTRE = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
const HANDLE_CLASS = 'opacity-0 !pointer-events-none';

type Props = {
  icon: ComponentType<{ className?: string }>;
  iconClassName: string;
  /** Solid fill of the block drawn when zoomed out. */
  compactClassName: string;
  /** Hover text of the block drawn when zoomed out. */
  label: string;
  selected: boolean;
  focused: boolean;
  /** Fixed width; must match `NODE_SIZE` in useGraphLayout so the layout spaces nodes correctly. */
  className: string;
  /** Real nodes: a right chevron marks a collapsed node that has children to show. */
  expandable?: boolean;
  /** Real nodes: set when the node is expanded and not the root; draws a down chevron that collapses it. */
  onCollapse?: () => void;
  /** A group card: a dashed border and no shadow, so it reads as structure rather than as an entity. */
  grouping?: boolean;
  /**
   * The card's lines, stacked with the same gap on every kind. The first is the title line: it alone shows between
   * COMPACT_ZOOM and SUBTITLE_ZOOM, without any element in it marked `data-detail` (a badge, the mention count);
   * the rest keep their space, so the card keeps its size.
   */
  children: ReactNode;
};

/** Card, icon, and hidden edge handles shared by every graph node kind. */
export function NodeShell({
  icon: Icon,
  iconClassName,
  compactClassName,
  label,
  selected,
  focused,
  className,
  expandable,
  onCollapse,
  grouping = false,
  children,
}: Props) {
  // Zoomed out, the card shows its title line only, then draws as a block in its kind's colour.
  const detail = useStore(selectZoomDetail);
  const compact = detail === 'compact';
  return (
    <div
      title={compact ? label : undefined}
      className={clsx(
        'rounded-lg border px-3 py-2',
        compact
          ? clsx(
              'border-transparent',
              compactClassName,
              // A thin border or ring shrinks out of sight when zoomed out; a thick outline stays visible.
              (focused || selected) && 'outline-8 outline-offset-4 outline-solid',
              focused ? 'outline-indigo-500' : selected && 'outline-gray-900 dark:outline-white',
            )
          : clsx(
              'bg-white text-gray-900 dark:bg-zinc-800 dark:text-zinc-100',
              grouping ? 'border-dashed' : 'shadow-sm dark:shadow-zinc-950/30',
              // The same three states on every kind: selected is an indigo border, the root a ring, compact a block.
              selected
                ? 'border-indigo-500 dark:border-indigo-400'
                : grouping
                  ? 'border-zinc-300 dark:border-zinc-600'
                  : 'border-gray-200 dark:border-zinc-700',
              // The focus ring wins over the fainter selection ring when a node is both.
              focused
                ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-indigo-400 dark:ring-offset-zinc-900'
                : selected && 'ring-2 ring-indigo-500/30 dark:ring-indigo-400/30',
            ),
        className,
      )}
    >
      <Handle type="target" position={Position.Top} isConnectable={false} style={CENTRE} className={HANDLE_CLASS} />
      {/* Hidden, not removed, when compact or title-only: the card keeps its exact size at every zoom, so the
          measured size the fits read never changes and a threshold crossing measures nothing again. */}
      <div className={clsx('flex items-start gap-2', compact && 'invisible')}>
        <Icon className={clsx('mt-0.5 size-4 shrink-0', iconClassName)} />
        <div
          className={clsx(
            'flex min-w-0 flex-1 flex-col gap-0.5',
            detail === 'titleOnly' && '*:not-first:invisible **:data-detail:invisible',
          )}
        >
          {children}
        </div>
        {onCollapse ? (
          <CollapseButton label={label} icon={ChevronDownIcon} onCollapse={onCollapse} />
        ) : (
          expandable && <ChevronRightIcon className="mt-0.5 size-3.5 shrink-0 text-gray-400 dark:text-zinc-500" />
        )}
      </div>
      <Handle type="source" position={Position.Bottom} isConnectable={false} style={CENTRE} className={HANDLE_CLASS} />
    </div>
  );
}
